import { parseJson } from '@gnsx/genesys.js';

import packageJson from '../../../package.json';

import { ScenarioConfig } from './scenarioConfig';
import { ModelCapabilities } from './scenarioConstants';
import { parseAPIError, ScenarioAPIError, shouldRetry as shouldRetryError } from './scenarioErrors';

import type { ScenarioRequestContext } from './scenarioErrors';
import type {
  ScenarioAsset,
  ScenarioAssetsResponse,
  ScenarioControlNetTextureParams,
  ScenarioForwardRequest,
  ScenarioImageModel,
  ScenarioImageParams,
  ScenarioImg2ImgTextureParams,
  ScenarioInferenceResponse,
  ScenarioJobResponse,
  ScenarioModel3D,
  ScenarioModel3DJobResponse,
  ScenarioModel3DParams,
  ScenarioModelInput,
  ScenarioModelsResponse,
  ScenarioPromptRequest,
  ScenarioPromptResponse,
  ScenarioSkyboxParams,
  ScenarioSkyboxUpscaleParams,
  ScenarioTextureConversionParams,
  ScenarioTextureParams,
} from './scenarioTypes';

const isDev = process.env.NODE_ENV === 'development';
const DEBUG = {
  logRetry: isDev,
  logDuplicateTag: isDev,
  logSkybox: isDev,
  logPromptEnhance: isDev,
  logAssetFetch: isDev,
  logAssetDownload: isDev,
  logTextureGeneration: isDev,
  logImageGeneration: isDev,
  logBackgroundRemoval: isDev,
  logModelFetch: isDev,
  logModelResult: isDev && false,
  logImageUpload: isDev,
  log3DGeneration: isDev,
} as const;

/** Track request timing and frequency for debugging */
const requestLog = new Map<string, { count: number; lastCall: number; errors: number }>();

/** Simple request debouncing to prevent flooding */
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Public API function with retry logic - this is what all callers should use
 * Implements exponential backoff as recommended by Scenario docs
 * https://docs.scenario.com/docs/understanding-api-responses-and-errors
 */
async function callScenarioAPI(request: ScenarioForwardRequest): Promise<any> {
  const maxRetries = ScenarioConfig.retry.maxRetries;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await callScenarioAPICore(request);
    } catch (error: any) {
      lastError = error;

      if (shouldRetryError(error, attempt, maxRetries)) {
        // Exponential backoff: 1s, 2s, 4s delays
        const delay = Math.pow(2, attempt) * ScenarioConfig.retry.backoffBaseMs;
        if (DEBUG.logRetry) {
          console.log(`🔄 Retrying Scenario API call in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1}).`);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
      } else if (error.message?.includes('500')) {
        console.error('❌ Max retries reached for 500 error. Per Scenario docs: Contact support if persistent.');
        break;
      } else {
        // Don't retry non-500 errors (4xx are client errors)
        throw error;
      }
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error(`Scenario API call failed unexpectedly: no result after ${maxRetries + 1} attempts (path: ${request.path})`);
}

/**
 * Core function to make direct Scenario API calls using the generic forwarder
 */
async function callScenarioAPICore(request: ScenarioForwardRequest): Promise<any> {
  const requestKey = `${request.method} ${request.path}`;
  const now = Date.now();

  // Create a more specific key for exact duplicate detection (include body for POST requests)
  const bodyHash = request.body ? JSON.stringify(request.body) : '';
  const duplicateKey = `${requestKey}:${bodyHash}`;

  // Check for duplicate requests and debounce them
  if (pendingRequests.has(duplicateKey)) {
    return pendingRequests.get(duplicateKey)!;
  }

  // Track request frequency
  if (!requestLog.has(requestKey)) {
    requestLog.set(requestKey, { count: 0, lastCall: 0, errors: 0 });
  }
  const stats = requestLog.get(requestKey)!;
  stats.count++;
  const timeSinceLastCall = now - stats.lastCall;
  stats.lastCall = now;

  // Add basic rate limiting
  if (timeSinceLastCall < ScenarioConfig.rateLimit.minIntervalMs && timeSinceLastCall > 0) {
    await new Promise(resolve =>
      setTimeout(resolve, ScenarioConfig.rateLimit.minIntervalMs - timeSinceLastCall)
    );
  }

  // Create the actual request promise and store it for debouncing
  const requestPromise = (async () => {
    try {
      const response = await fetch('/api/scenario', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      return response;
    } finally {
      // Clean up the pending request
      pendingRequests.delete(duplicateKey);
    }
  })();

  // Store the promise for potential debouncing
  pendingRequests.set(duplicateKey, requestPromise);

  const response = await requestPromise;

  if (!response.ok) {
    const errorText = await response.text();
    const stats = requestLog.get(requestKey)!;
    stats.errors++;

    // Check if this is a duplicate tag error (which is often expected and handled gracefully)
    const isDuplicateTag = response.status === 400 && errorText.includes('Duplicated tag');

    if (isDuplicateTag) {
      // Log duplicate tag errors as info instead of error since they're often expected
      if (DEBUG.logDuplicateTag) console.log('ℹ️ Duplicate tag detected (will be handled by caller):', errorText);
    } else if (response.status === 500) {
      // Per Scenario docs: 500 = server-side issue, implement retry logic
      console.error('🚨 SCENARIO SERVER ERROR (500) - Per docs: "server-side issue":', {
        status: response.status,
        statusText: response.statusText,
        path: request.path,
        method: request.method,
        errorText,
        recommendation: 'Scenario docs: Implement retry logic. Contact support if persistent',
        documentation: 'https://docs.scenario.com/docs/understanding-api-responses-and-errors',
      });
    } else {
      // Log other errors with context
      console.error('🌐 API Error response:', {
        status: response.status,
        statusText: response.statusText,
        path: request.path,
        method: request.method,
        errorText,
      });
    }

    throw parseAPIError(response.status, errorText, response);
  }

  const result = await response.json();
  return result;
}

/** localStorage cache configuration */
const STORAGE_KEY = 'genesys_scenario_models';
const CACHE_VERSION = packageJson.version; // Tied to editor version for automatic cache invalidation

interface StoredModelCache {
  version: string;
  allModels: any[] | null;
  model3DList: ScenarioModel3D[] | null;
}

/** localStorage persistence layer (L2 cache) */
const storageCache = {
  load(): StoredModelCache | null {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const parsed = parseJson(stored) as StoredModelCache;
      if (parsed.version !== CACHE_VERSION) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  },

  save(data: Partial<Omit<StoredModelCache, 'version'>>): void {
    if (typeof window === 'undefined') return;
    try {
      const existing = this.load() ?? { version: CACHE_VERSION, allModels: null, model3DList: null };
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...existing,
        ...data,
        version: CACHE_VERSION,
      }));
    } catch {
      // localStorage full or unavailable - silently fail
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore errors
    }
  },
};

/** Memory cache (L1 cache) - instant access during session */
const memoryCache = {
  allModels: null as any[] | null,
  allModelsLoading: false,
  model3DList: null as ScenarioModel3D[] | null,
  model3DLoading: false,

  clear(): void {
    this.allModels = null;
    this.model3DList = null;
  },
};

/** Combined L1/L2 cache accessor */
const clientCache = {
  getAllModels(): any[] | null {
    // L1: Check memory first
    if (memoryCache.allModels) return memoryCache.allModels;
    // L2: Check localStorage
    const stored = storageCache.load();
    if (stored?.allModels) {
      memoryCache.allModels = stored.allModels; // Promote to L1
      return stored.allModels;
    }
    return null;
  },

  getModel3DList(): ScenarioModel3D[] | null {
    // L1: Check memory first
    if (memoryCache.model3DList) return memoryCache.model3DList;
    // L2: Check localStorage
    const stored = storageCache.load();
    if (stored?.model3DList) {
      memoryCache.model3DList = stored.model3DList; // Promote to L1
      return stored.model3DList;
    }
    return null;
  },

  setAllModels(models: any[]): void {
    memoryCache.allModels = models;
    if (DEBUG.logModelFetch) console.log('🧊 Setting all models in memory cache:', models);
    storageCache.save({ allModels: models });
  },

  setModel3D(models: ScenarioModel3D[]): void {
    memoryCache.model3DList = models;
    storageCache.save({ model3DList: models });
  },

  clearAll(): void {
    memoryCache.clear();
    storageCache.clear();
  },

  get allModelsLoading() { return memoryCache.allModelsLoading; },
  set allModelsLoading(v: boolean) { memoryCache.allModelsLoading = v; },
  get model3DLoading() { return memoryCache.model3DLoading; },
  set model3DLoading(v: boolean) { memoryCache.model3DLoading = v; },
};

/**
 * Scenario API client for image generation
 * Contains only direct API call methods
 */
export class ScenarioClient {
  /** Convert width/height to third-party model compatible aspect ratio string */
  private getAspectRatioString(width: number, height: number): string {
    const ratio = width / height;
    // Map to supported aspect ratios per docs:
    // https://docs.scenario.com/docs/image-generation-models-parameters-reference#gemini-30-pro
    // Gemini supports: 21:9, 16:9, 3:2, 4:3, 5:4, 1:1, 4:5, 3:4, 2:3, 9:16, auto
    if (ratio >= 2.2) return '21:9';      // 2.33
    if (ratio >= 1.7) return '16:9';      // 1.78
    if (ratio >= 1.4) return '3:2';       // 1.5
    if (ratio >= 1.25) return '4:3';      // 1.33
    if (ratio >= 1.1) return '5:4';       // 1.25
    if (ratio >= 0.9) return '1:1';       // 1.0
    if (ratio >= 0.75) return '4:5';      // 0.8
    if (ratio >= 0.7) return '3:4';       // 0.75
    if (ratio >= 0.6) return '2:3';       // 0.67
    return '9:16';                         // 0.56
  }

  /**
   * Get all models with auto-pagination and caching
   * This is the base method that texture/image/3D model getters use
   */
  async getAllModels(options?: { forceRefresh?: boolean }): Promise<any[]> {
    if (options?.forceRefresh) clientCache.clearAll();

    // Check L1/L2 cache
    const cached = clientCache.getAllModels();
    if (cached) {
      if (DEBUG.logModelResult) console.log('📦 Returning all cached models.', cached);
      return cached;
    }

    // Wait if already loading
    if (clientCache.allModelsLoading) {
      if (DEBUG.logModelFetch) console.log('⏳ Models (all) already loading, waiting...');
      while (clientCache.allModelsLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return clientCache.getAllModels() ?? [];
    }

    clientCache.allModelsLoading = true;
    try {
      if (DEBUG.logModelFetch) console.log('🔄 Fetching all models with pagination...');

      const allModels: any[] = [];
      let nextToken: string | undefined;

      // Auto-paginate through all results
      do {
        const response = await callScenarioAPI({
          method: 'GET',
          path: '/models',
          queryParams: {
            privacy: 'public',
            pageSize: '200',
            ...(nextToken && { paginationToken: nextToken }),
          },
        });

        const models = response.models ?? [];
        allModels.push(...models);
        nextToken = response.nextPaginationToken;

        if (DEBUG.logModelFetch) console.log(`📊 Fetched ${models.length} models (total: ${allModels.length})${nextToken ? ', more available...' : ''}`);
      } while (nextToken);

      if (DEBUG.logModelFetch) console.log(`✅ Fetched ${allModels.length} total models.`);

      clientCache.setAllModels(allModels);
      return allModels;
    } catch (error) {
      console.error('❌ Failed to fetch all models:', error);
      return [];
    } finally {
      clientCache.allModelsLoading = false;
    }
  }

  /**
   * Get job status and results
   */
  async getJob(jobId: string): Promise<ScenarioJobResponse> {
    // https://docs.scenario.com/reference/getjobid
    return callScenarioAPI({
      method: 'GET',
      path: `/jobs/${jobId}`,
    });
  }

  /**
   * Create a skybox generation request
   */
  async createSkyboxInference(params: ScenarioSkyboxParams): Promise<ScenarioInferenceResponse> {
    // Build the request body
    const requestBody: any = {
      prompt: params.prompt,
      style: params.style,
      seed: params.seed,
      numOutputs: params.numOutputs ?? ScenarioConfig.defaults.skybox.numOutputs,
      numInferenceSteps: params.numInferenceSteps ?? ScenarioConfig.defaults.skybox.numInferenceSteps,
      geometryEnforcement: params.geometryEnforcement ?? ScenarioConfig.defaults.skybox.geometryEnforcement,
    };

    // Add reference image if provided
    if (params.referenceImage?.id) {
      if (DEBUG.logSkybox) {
        console.log('🖼️ Including reference image in skybox generation:', params.referenceImage.id);
        console.log('🎚️ Reference image mode:', params.referenceImage.mode);
        console.log('🎚️ Reference image influence:', params.referenceImage.influence);
      }

      if (params.referenceImage.mode === 'controlnet') {
        requestBody.controlImage = params.referenceImage.id;
        requestBody.controlMode = 'depth';
      } else {
        requestBody.image = params.referenceImage.id;
      }

      if (params.referenceImage.influence !== undefined) {
        requestBody.influence = params.referenceImage.influence / 100;
        if (DEBUG.logSkybox) console.log('🎚️ Converted influence:', params.referenceImage.influence, '% to', requestBody.influence);
      }

      if (params.referenceImage.mode) {
        requestBody.referenceMode = params.referenceImage.mode;
      }
    }

    if (DEBUG.logSkybox) console.log('📤 Sending skybox generation request:', requestBody);

    try {
      const response = await callScenarioAPI({
        method: 'POST',
        path: '/generate/skybox-base-360',
        body: requestBody,
      });

      if (DEBUG.logSkybox) console.log('📥 Skybox generation response:', response);
      return response;
    } catch (error) {
      console.error('❌ Skybox generation failed:', error);
      throw error;
    }
  }

  /**
   * Upscale a skybox image
   */
  async upscaleSkybox(params: ScenarioSkyboxUpscaleParams): Promise<ScenarioInferenceResponse> {
    // https://docs.scenario.com/reference/postskyboxupscale360inferences
    return callScenarioAPI({
      method: 'POST',
      path: '/generate/skybox-upscale-360',
      body: {
        image: params.assetId,
        scalingFactor: params.scalingFactor ?? ScenarioConfig.defaults.skyboxUpscale.scalingFactor,
      },
    });
  }

  /**
   * Convert a texture to multiple texture maps (normal, height, metallic, etc.)
   */
  async convertTexture(params: ScenarioTextureConversionParams): Promise<ScenarioInferenceResponse> {
    // https://docs.scenario.com/reference/posttextureinferences
    return callScenarioAPI({
      method: 'POST',
      path: '/generate/texture',
      body: {
        texture: params.sourceAssetId,
        raised: params.raised ?? ScenarioConfig.defaults.textureConversion.raised,
        shiny: params.shiny ?? ScenarioConfig.defaults.textureConversion.shiny,
        polished: params.polished ?? ScenarioConfig.defaults.textureConversion.polished,
        angular: params.angular ?? ScenarioConfig.defaults.textureConversion.angular,
        invert: params.invert ?? ScenarioConfig.defaults.textureConversion.invert,
      },
    });
  }

  /**
   * Generate or enhance prompts using Scenario's prompt generation
   */
  async enhancePrompt(request: ScenarioPromptRequest): Promise<ScenarioPromptResponse> {
    // https://docs.scenario.com/reference/postpromptinferences
    if (DEBUG.logPromptEnhance) {
      console.log('🪄 Enhancing prompt with context:', {
        promptLength: request.prompt.length,
        mode: request.mode,
        hasModel: !!request.modelId,
        hasImage: !!request.image,
        hasImages: !!request.images?.length,
      });
    }

    const requestBody: any = {
      prompt: request.prompt,
      numResults: request.numResults || 1,
    };

    // Add optional parameters
    if (request.mode) requestBody.mode = request.mode;
    if (request.modelId) requestBody.modelId = request.modelId;
    if (request.image) requestBody.image = request.image;
    if (request.images && request.images.length > 0) requestBody.images = request.images;
    if (request.context) requestBody.context = request.context;

    const result = await callScenarioAPI({
      method: 'POST',
      path: '/generate/prompt',
      body: requestBody,
    });

    if (DEBUG.logPromptEnhance) {
      console.log('✨ Prompt enhancement result:', {
        promptsReturned: result.prompts?.length || 0,
        originalLength: request.prompt.length,
        enhancedLength: result.prompts?.[0]?.length || 0,
      });
    }

    // Handle the response structure - extract the first prompt from the array
    if (result && result.prompts && Array.isArray(result.prompts) && result.prompts.length > 0) {
      const enhancedPrompt = result.prompts[0];
      return { prompt: enhancedPrompt };
    } else {
      console.error('Unexpected response structure from prompt enhancement:', result);
      throw new Error(`Prompt enhancement failed: unexpected response structure (got ${JSON.stringify(result).slice(0, 200)})`);
    }
  }

  /**
   * List all assets (historical generations) for the project
   */
  async listAssets(params?: {
    limit?: number;
    offset?: number;
    type?: string;
    filter?: 'skybox' | 'texture' | 'image';
    tags?: string[];
  }): Promise<ScenarioAssetsResponse> {
    // https://docs.scenario.com/reference/getassets
    const queryParams: Record<string, string | string[]> = {};

    queryParams.limit = (params?.limit ?? 1000).toString();
    if (params?.offset) queryParams.offset = params.offset.toString();
    if (params?.type) queryParams.type = params.type;
    if (params?.tags && params.tags.length > 0) queryParams.tags = params.tags.join(',');

    const result = await callScenarioAPI({
      method: 'GET',
      path: '/assets',
      queryParams,
    });

    if (DEBUG.logAssetFetch) console.log(`📦 Fetched ${result.assets?.length ?? 0} total assets from Scenario API. result:`, result);

    return result;
  }

  /**
   * Get an asset by ID
   */
  async getAsset(assetId: string): Promise<ScenarioAsset> {
    // https://docs.scenario.com/reference/getassetsbyassetid
    const response = await callScenarioAPI({
      method: 'GET',
      path: `/assets/${assetId}`,
    });

    return response.asset;
  }

  /**
   * Download an asset by ID directly
   */
  async downloadAsset(assetId: string): Promise<Blob> {
    const assetData = await this.getAsset(assetId);
    const imageUrl = assetData.url;

    if (!imageUrl) {
      console.error('No image URL found in asset metadata:', assetData);
      throw new Error(`No image URL found in asset metadata (asset ID: ${assetId}, type: ${assetData.metadata?.type ?? 'unknown'})`);
    }

    if (DEBUG.logAssetDownload) console.log('Downloading image from URL:', imageUrl);

    const imageResponse = await fetch(imageUrl);

    if (!imageResponse.ok) {
      throw new Error(`Failed to download image: ${imageResponse.status} - ${imageResponse.statusText}`);
    }

    return imageResponse.blob();
  }

  /**
   * Generate texture using text-to-texture
   */
  async generateTexture(params: ScenarioTextureParams): Promise<ScenarioInferenceResponse> {
    try {
      if (DEBUG.logTextureGeneration) console.log('🚀 Starting texture generation with params:', params);

      // https://docs.scenario.com/reference/posttxt2imgtextureinferences
      const requestBody = {
        modelId: params.modelId,
        prompt: params.prompt,
        seed: params.seed,
        numSamples: params.numSamples ?? ScenarioConfig.defaults.texture.numSamples,
        numInferenceSteps: params.numInferenceSteps ?? ScenarioConfig.defaults.texture.numInferenceSteps,
        guidance: params.guidance ?? ScenarioConfig.defaults.texture.guidance,
        width: params.width ?? ScenarioConfig.defaults.texture.width,
        height: params.height ?? ScenarioConfig.defaults.texture.height,
        tiling: params.tiling ?? ScenarioConfig.defaults.texture.tiling,
        seamless: params.seamless ?? ScenarioConfig.defaults.texture.seamless,
      };

      if (DEBUG.logTextureGeneration) console.log('📤 Sending request body:', requestBody);

      const response = await callScenarioAPI({
        method: 'POST',
        path: '/generate/txt2img-texture',
        body: requestBody,
      });

      // Extract jobId from the nested job object or try other possible locations
      const jobId = response.job?.jobId ?? response.jobId ?? response.id ?? response.inferenceId ?? undefined;

      const normalizedResponse = {
        ...response,
        jobId: jobId,
      };

      return normalizedResponse;
    } catch (error) {
      console.error('❌ Texture generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate texture using Image-to-Image mode with reference image
   */
  async generateImg2ImgTexture(params: ScenarioImg2ImgTextureParams): Promise<ScenarioInferenceResponse> {
    try {
      if (DEBUG.logTextureGeneration) console.log('🚀 Starting img2img texture generation with params:', params);

      // https://docs.scenario.com/reference/postimg2imgtextureinferences
      const requestBody = {
        modelId: params.modelId,
        prompt: params.prompt,
        image: params.image,
        influence: params.influence ?? ScenarioConfig.defaults.img2img.influence,
        seed: params.seed,
        numSamples: params.numSamples ?? ScenarioConfig.defaults.texture.numSamples,
        numInferenceSteps: params.numInferenceSteps ?? ScenarioConfig.defaults.texture.numInferenceSteps,
        guidance: params.guidance ?? ScenarioConfig.defaults.texture.guidance,
        width: params.width ?? ScenarioConfig.defaults.texture.width,
        height: params.height ?? ScenarioConfig.defaults.texture.height,
        tiling: params.tiling ?? ScenarioConfig.defaults.texture.tiling,
        seamless: params.seamless ?? ScenarioConfig.defaults.texture.seamless,
      };

      if (DEBUG.logTextureGeneration) console.log('📤 Sending img2img request body:', requestBody);

      const response = await callScenarioAPI({
        method: 'POST',
        path: '/generate/img2img-texture',
        body: requestBody,
      });

      const jobId = response.job?.jobId ?? response.jobId ?? response.id ?? response.inferenceId ?? undefined;

      const normalizedResponse = {
        ...response,
        jobId: jobId,
      };
      return normalizedResponse;
    } catch (error) {
      console.error('❌ Img2Img texture generation failed:', error);
      throw error;
    }
  }

  /**
   * Generate texture using ControlNet mode with reference image
   */
  async generateControlNetTexture(params: ScenarioControlNetTextureParams): Promise<ScenarioInferenceResponse> {
    try {
      if (DEBUG.logTextureGeneration) console.log('🚀 Starting controlnet texture generation with params:', params);

      // https://docs.scenario.com/reference/postcontrolnettextureinferences
      const requestBody = {
        modelId: params.modelId,
        prompt: params.prompt,
        controlImage: params.controlImage,
        influence: params.influence ?? ScenarioConfig.defaults.img2img.influence,
        seed: params.seed,
        numSamples: params.numSamples ?? ScenarioConfig.defaults.texture.numSamples,
        numInferenceSteps: params.numInferenceSteps ?? ScenarioConfig.defaults.texture.numInferenceSteps,
        guidance: params.guidance ?? ScenarioConfig.defaults.texture.guidance,
        width: params.width ?? ScenarioConfig.defaults.texture.width,
        height: params.height ?? ScenarioConfig.defaults.texture.height,
        tiling: params.tiling ?? ScenarioConfig.defaults.texture.tiling,
        seamless: params.seamless ?? ScenarioConfig.defaults.texture.seamless,
      };

      if (DEBUG.logTextureGeneration) console.log('📤 Sending controlnet request body:', requestBody);

      const response = await callScenarioAPI({
        method: 'POST',
        path: '/generate/controlnet-texture',
        body: requestBody,
      });

      const jobId = response.job?.jobId ?? response.jobId ?? response.id ?? response.inferenceId ?? undefined;

      const normalizedResponse = {
        ...response,
        jobId: jobId,
      };
      return normalizedResponse;
    } catch (error) {
      console.error('❌ ControlNet texture generation failed:', error);
      throw error;
    }
  }

  /**
   * Tag assets with specified tags
   */
  async tagAssets(assetIds: string[], tags: string[]): Promise<void> {
    // https://docs.scenario.com/reference/putassetstagsbyassetid
    for (const assetId of assetIds) {
      await callScenarioAPI({
        method: 'PUT',
        path: `/assets/${assetId}/tags`,
        body: { add: tags, delete: [] },
      });
    }
  }

  /**
   * Get available models
   */
  async getModels(): Promise<ScenarioModelsResponse> {
    // https://docs.scenario.com/reference/getmodels
    return callScenarioAPI({
      method: 'GET',
      path: '/models',
    });
  }

  /**
   * Get models in bulk by IDs
   */
  async getModelsBulk(modelIds?: string[]): Promise<ScenarioModelsResponse> {
    // https://docs.scenario.com/reference/postmodelsgetbulk
    return callScenarioAPI({
      method: 'POST',
      path: '/models/get-bulk',
      body: {
        minimal: false,
        modelIds: modelIds ?? ScenarioConfig.models.textureModelIds,
      },
    });
  }

  /**
   * Generate images using text-to-image
   * Dynamically builds request body based on model's supported inputs
   */
  async generateImage(params: ScenarioImageParams): Promise<ScenarioInferenceResponse> {
    // Custom models use /generate/custom/{modelId}, base models use /generate/txt2img
    const isCustomModel = params.modelType === 'custom';
    const path = isCustomModel ? `/generate/custom/${params.modelId}` : '/generate/txt2img';

    try {
      if (DEBUG.logImageGeneration) console.log('🚀 Starting image generation with params:', params);

      // Build set of supported params from model inputs
      const supportedParams = new Set(params.modelInputs?.map(i => i.name) ?? []);
      const hasInputs = supportedParams.size > 0;

      // Build request body dynamically based on model's supported inputs
      const requestBody: Record<string, unknown> = {
        prompt: params.prompt,
      };

      // Only include modelId for base models using /generate/txt2img endpoint
      // For custom models, modelId is already in the URL path
      if (!isCustomModel) {
        requestBody.modelId = params.modelId;
      }

      // Handle aspect ratio vs width/height based on model support
      if (hasInputs) {
        // Use model inputs to determine which params to include
        if (supportedParams.has('aspectRatio')) {
          // Use direct aspectRatio if provided (from dropdown), otherwise convert from width/height
          if (params.aspectRatio) {
            requestBody.aspectRatio = params.aspectRatio;
          } else {
            const width = params.width ?? ScenarioConfig.defaults.image.width;
            const height = params.height ?? ScenarioConfig.defaults.image.height;
            requestBody.aspectRatio = this.getAspectRatioString(width, height);
          }
        } else {
          // Fall back to width/height for models that support them
          if (supportedParams.has('width')) requestBody.width = params.width ?? ScenarioConfig.defaults.image.width;
          if (supportedParams.has('height')) requestBody.height = params.height ?? ScenarioConfig.defaults.image.height;
        }

        // Add other params only if supported by the model
        if (supportedParams.has('seed') && params.seed !== undefined) requestBody.seed = params.seed;

        // Handle numSamples vs numOutputs (different models use different param names)
        if (supportedParams.has('numSamples') || supportedParams.has('numOutputs')) {
          const key = supportedParams.has('numOutputs') ? 'numOutputs' : 'numSamples';
          requestBody[key] = params.numSamples ?? ScenarioConfig.defaults.image.numSamples;
        }

        // Handle numInferenceSteps vs steps (different models use different param names)
        if (supportedParams.has('numInferenceSteps') || supportedParams.has('steps')) {
          const key = supportedParams.has('numInferenceSteps') ? 'numInferenceSteps' : 'steps';
          requestBody[key] = params.numInferenceSteps ?? ScenarioConfig.defaults.image.numInferenceSteps;
        }

        // Handle guidance vs guidanceScale (different models use different param names)
        if (supportedParams.has('guidance') || supportedParams.has('guidanceScale')) {
          const key = supportedParams.has('guidanceScale') ? 'guidanceScale' : 'guidance';
          requestBody[key] = params.guidance ?? ScenarioConfig.defaults.image.guidance;
        }

        // Handle reference images based on model support
        // Different models use different param names for reference images:
        // - referenceImages: Flux 2, Gemini, Seedream 4.5, Reve Remix
        // - images: Seedream 4
        // - imagePrompt: FLUX1.1 Pro Ultra/Pro
        // - image: FLUX.1 Krea Dev, Retrodiffusion
        // - imageReferenceUrl: Luma Photon
        // - subjectReference: Minimax
        // - characterReferenceImage: Ideogram Character
        if (params.referenceImage?.id) {
          if (DEBUG.logImageGeneration) console.log('🖼️ Including reference image in image generation:', params.referenceImage.id);

          if (supportedParams.has('referenceImages')) {
            requestBody.referenceImages = [params.referenceImage.id];
          } else if (supportedParams.has('images')) {
            requestBody.images = [params.referenceImage.id];
          } else if (supportedParams.has('imagePrompt')) {
            requestBody.imagePrompt = params.referenceImage.id;
          } else if (supportedParams.has('imageReferenceUrl')) {
            requestBody.imageReferenceUrl = params.referenceImage.id;
          } else if (supportedParams.has('subjectReference')) {
            requestBody.subjectReference = params.referenceImage.id;
          } else if (supportedParams.has('characterReferenceImage')) {
            requestBody.characterReferenceImage = params.referenceImage.id;
          } else if (supportedParams.has('image')) {
            if (params.referenceImage.mode === 'controlnet' && supportedParams.has('controlImage')) {
              requestBody.controlImage = params.referenceImage.id;
              if (supportedParams.has('controlMode')) requestBody.controlMode = 'depth';
            } else {
              requestBody.image = params.referenceImage.id;
            }
          }

          // Handle influence/strength params (different models use different names)
          // - influence: standard
          // - promptStrength: FLUX.1 Krea Dev
          // - imageReferenceWeight: Luma Photon
          // - strength: Retrodiffusion Plus
          if (params.referenceImage.influence !== undefined) {
            const influenceValue = params.referenceImage.influence / 100;
            if (supportedParams.has('influence')) {
              requestBody.influence = influenceValue;
            } else if (supportedParams.has('promptStrength')) {
              requestBody.promptStrength = influenceValue;
            } else if (supportedParams.has('imageReferenceWeight')) {
              requestBody.imageReferenceWeight = influenceValue;
            } else if (supportedParams.has('strength')) {
              requestBody.strength = influenceValue;
            }
          }
        }

        // Add dynamic params (resolution, useGoogleSearch, raw, promptUpsampling, etc.)
        if (params.dynamicParams) {
          for (const [key, value] of Object.entries(params.dynamicParams)) {
            if (supportedParams.has(key) && value !== undefined && value !== '') {
              requestBody[key] = value;
            }
          }
        }
      } else {
        // Fallback for models without input definitions (use standard diffusion params)
        requestBody.modelId = params.modelId;
        requestBody.seed = params.seed;
        requestBody.numSamples = params.numSamples ?? ScenarioConfig.defaults.image.numSamples;
        requestBody.numInferenceSteps = params.numInferenceSteps ?? ScenarioConfig.defaults.image.numInferenceSteps;
        requestBody.guidance = params.guidance ?? ScenarioConfig.defaults.image.guidance;
        requestBody.width = params.width ?? ScenarioConfig.defaults.image.width;
        requestBody.height = params.height ?? ScenarioConfig.defaults.image.height;

        // Add reference image for fallback path
        if (params.referenceImage?.id) {
          if (DEBUG.logImageGeneration) console.log('🖼️ Including reference image in image generation:', params.referenceImage.id);

          if (params.referenceImage.mode === 'controlnet') {
            requestBody.controlImage = params.referenceImage.id;
            requestBody.controlMode = 'depth';
          } else {
            requestBody.image = params.referenceImage.id;
          }

          if (params.referenceImage.influence !== undefined) {
            requestBody.influence = params.referenceImage.influence / 100;
          }
        }
      }

      if (DEBUG.logImageGeneration) console.log('📤 Sending image generation request:', { path, requestBody, supportedParams: Array.from(supportedParams) });

      const response = await callScenarioAPI({
        method: 'POST',
        path,
        body: requestBody,
      });

      if (DEBUG.logImageGeneration) console.log('📥 Image generation response:', response);

      const jobId = response.job?.jobId ?? response.jobId ?? response.id ?? response.inferenceId ?? undefined;

      return {
        ...response,
        jobId: jobId,
      };
    } catch (error) {
      console.error('❌ Image generation failed:', error);

      // Add request context to the error for better debugging
      const requestContext: ScenarioRequestContext = {
        modelId: params.modelId,
        modelName: params.modelName,
        endpoint: path,
        params: { prompt: params.prompt, width: params.width, height: params.height },
      };

      if (error instanceof ScenarioAPIError) {
        error.requestContext = requestContext;
        throw error;
      }

      // Wrap non-ScenarioAPIError in a ScenarioAPIError with context
      throw new ScenarioAPIError(
        error instanceof Error ? error.message : String(error),
        undefined,
        undefined,
        requestContext
      );
    }
  }

  /**
   * Remove background from an image
   */
  async removeBackground(assetId: string): Promise<ScenarioInferenceResponse> {
    try {
      if (DEBUG.logBackgroundRemoval) console.log('🚀 Starting background removal for asset:', assetId);

      // https://docs.scenario.com/reference/generateremovebackground
      const response = await callScenarioAPI({
        method: 'POST',
        path: '/generate/remove-background',
        body: {
          image: assetId,
        },
      });

      if (DEBUG.logBackgroundRemoval) console.log('📥 Background removal response:', response);

      const jobId = response.job?.jobId ?? response.jobId ?? response.id ?? response.inferenceId ?? undefined;

      return {
        ...response,
        jobId: jobId,
      };
    } catch (error) {
      console.error('❌ Background removal failed:', error);
      throw error;
    }
  }

  /**
   * Get texture models - filters from unified cache
   * Includes models with texture capabilities, excludes other types
   */
  async getTextureModels(options?: { forceRefresh?: boolean }): Promise<ScenarioImageModel[]> {
    const allModels = await this.getAllModels(options);

    const excludedCapabilities = new Set<string>([
      // ...ModelCapabilities.image,
      ...ModelCapabilities.model3D,
      ...ModelCapabilities.model3DExcluded,
      ...ModelCapabilities.video,
      ...ModelCapabilities.audio,
    ]);
    const textureModels = allModels.filter((model: any) => {
      const caps: string[] = model.capabilities ?? [];

      // only include models with sc:texture tag
      if (!model.tags?.includes('sc:texture')) return false;

      return ModelCapabilities.texture.some((c: string) => caps.includes(c)) && !caps.some((c: string) => excludedCapabilities.has(c));
    });

    // Sort featured first, then newest to oldest
    const sortedModels = textureModels.sort((a: any, b: any) => {
      const aFeatured = a.featured ?? a.tags?.includes('sc:featured') ?? false;
      const bFeatured = b.featured ?? b.tags?.includes('sc:featured') ?? false;
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bDate - aDate;
    });

    const result = sortedModels.map((model: any) => ({
      id: model.id,
      name: model.name,
      type: model.type,
      description: model.shortDescription,
      capabilities: model.capabilities,
      featured: model.featured ?? model.tags?.includes('sc:featured') ?? false,
      thumbnail: model.thumbnail,
      tags: model.tags,
      createdAt: model.createdAt,
      updatedAt: model.updatedAt,
    }));

    if (DEBUG.logModelResult) console.log(`🎨 Returning ${result.length} texture models:`, result);
    return result;
  }

  /**
   * Get image generation models - filters from unified cache
   * Includes models with image capabilities, excludes other types
   * Note: Inputs are fetched lazily via getModelInputs() when a model is selected
   */
  async getImageModels(options?: { forceRefresh?: boolean }): Promise<ScenarioImageModel[]> {
    const allModels = await this.getAllModels(options);

    const excludedCapabilities = new Set<string>([
      // image model includes ones with texture capabilities
      ...ModelCapabilities.model3D,
      ...ModelCapabilities.model3DExcluded,
      ...ModelCapabilities.video,
      ...ModelCapabilities.audio,
    ]);
    const imageModels = allModels.filter((model: any) => {
      const caps: string[] = model.capabilities ?? [];
      return ModelCapabilities.image.some((c: string) => caps.includes(c)) && !caps.some((c: string) => excludedCapabilities.has(c));
    });

    // Sort featured first, then newest to oldest
    const sortedModels = imageModels.sort((a: any, b: any) => {
      const aFeatured = a.featured ?? a.tags?.includes('sc:featured') ?? false;
      const bFeatured = b.featured ?? b.tags?.includes('sc:featured') ?? false;
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bDate - aDate;
    });

    // Map to ScenarioImageModel without fetching inputs (done lazily on selection)
    const result: ScenarioImageModel[] = sortedModels.map((model: any) => ({
      id: model.id,
      name: model.name,
      type: model.type,
      description: model.shortDescription,
      capabilities: model.capabilities,
      featured: model.featured ?? model.tags?.includes('sc:featured') ?? false,
      thumbnail: model.thumbnail,
      tags: model.tags,
      inputs: undefined,
      supportedParams: [],
      createdAt: model.createdAt,
      updatedAt: model.updatedAt,
    }));

    if (DEBUG.logModelResult) console.log(`🖼️ Returning ${result.length} image models:`, result);
    return result;
  }

  /**
   * Fetch model inputs/parameters for a specific model
   * Called lazily when a model is selected to avoid overwhelming the API
   */
  async getModelInputs(modelId: string): Promise<ScenarioModelInput[] | undefined> {
    try {
      const modelDetails = await callScenarioAPI({
        method: 'GET',
        path: `/models/${modelId}`,
      });
      return modelDetails.model?.inputs;
    } catch (error) {
      if (DEBUG.logModelFetch) console.error(`❌ Failed to fetch inputs for model ${modelId}:`, error);
      return undefined;
    }
  }

  /**
   * Upload an image file to Scenario and get asset ID
   */
  async uploadImage(imageFile: File | Blob, name?: string): Promise<string> {
    try {
      if (DEBUG.logImageUpload) {
        console.log('📤 Uploading image to Scenario...');
        console.log('📁 File details:', {
          name: imageFile instanceof File ? imageFile.name : 'blob',
          size: imageFile.size,
          type: imageFile.type,
          customName: name,
        });
      }

      const formData = new FormData();
      formData.append('file', imageFile);
      if (name) {
        formData.append('name', name);
      }

      // Use the unified scenario API route (automatically detects upload via content-type)
      const response = await fetch('/api/scenario', {
        method: 'POST',
        body: formData,
      });

      if (DEBUG.logImageUpload) {
        console.log('📥 Client received response:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload response error:', errorText);
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return result.asset.id;
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      throw error;
    }
  }

  /**
   * Get available 3D generation models - dynamic discovery from API
   * https://docs.scenario.com/docs/3d-models-parameters-reference
   * Uses getAllModels as base, then fetches detailed inputs for each model
   */
  async get3DModels(options?: { forceRefresh?: boolean }): Promise<ScenarioModel3D[]> {
    if (options?.forceRefresh) clientCache.clearAll();

    // Check L1/L2 cache
    const cached = clientCache.getModel3DList();
    if (cached) {
      if (DEBUG.logModelResult) console.log('📦 Returning cached 3D models.', cached);
      return cached;
    }

    // Wait if already loading
    if (clientCache.model3DLoading) {
      if (DEBUG.logModelFetch) console.log('⏳ 3D models already loading, waiting...');
      while (clientCache.model3DLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return clientCache.getModel3DList() ?? [];
    }

    clientCache.model3DLoading = true;
    try {
      if (DEBUG.logModelFetch) console.log('🧊 Fetching 3D models...');

      // Use getAllModels as base (shares cache with texture/image models)
      const allModels = await this.getAllModels(options);

      // Filter to only 3D models: must have model3D capability, exclude other types and excluded capabilities
      const excludedCapabilities = new Set<string>([
        ...ModelCapabilities.model3DExcluded,
        ...ModelCapabilities.image,
        ...ModelCapabilities.texture,
        ...ModelCapabilities.video,
        ...ModelCapabilities.audio,
      ]);
      const models = allModels.filter((model: any) => {
        const caps: string[] = model.capabilities ?? [];
        return ModelCapabilities.model3D.some((c: string) => caps.includes(c)) && !caps.some((c: string) => excludedCapabilities.has(c));
      });

      if (models.length === 0) {
        console.warn('⚠️ No 3D models found.');
        clientCache.setModel3D([]);
        return [];
      }

      // Sort: Featured first, then Hunyuan (except 2.1 last), then newest to oldest
      const sortedModels = models.sort((a: any, b: any) => {
        const aFeatured = a.featured ?? false;
        const bFeatured = b.featured ?? false;
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;

        // Hunyuan 2.1 goes to the very end
        const aIsHunyuan21 = a.name?.includes('2.1');
        const bIsHunyuan21 = b.name?.includes('2.1');
        if (aIsHunyuan21 && !bIsHunyuan21) return 1;
        if (!aIsHunyuan21 && bIsHunyuan21) return -1;

        // Other Hunyuan models come first
        const aIsHunyuan = a.name?.toLowerCase().includes('hunyuan') || a.id?.toLowerCase().includes('hunyuan');
        const bIsHunyuan = b.name?.toLowerCase().includes('hunyuan') || b.id?.toLowerCase().includes('hunyuan');
        if (aIsHunyuan && !bIsHunyuan) return -1;
        if (!aIsHunyuan && bIsHunyuan) return 1;


        // sort newest to oldest
        const aUpdatedAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bUpdatedAt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bUpdatedAt - aUpdatedAt;
      });

      // Fetch detailed info for each 3D model to get the inputs array
      const detailedModels: ScenarioModel3D[] = await Promise.all(
        sortedModels.map(async (model: any) => {
          try {
            const modelDetails = await callScenarioAPI({
              method: 'GET',
              path: `/models/${model.id}`,
            });

            const inputs = modelDetails.model?.inputs;
            return {
              id: model.id,
              name: model.name,
              type: model.type,
              capabilities: model.capabilities,
              description: model.shortDescription ?? modelDetails.model?.shortDescription,
              featured: !!model.featured,
              thumbnail: model.thumbnail ?? modelDetails.model?.thumbnail,
              tags: model.tags ?? modelDetails.model?.tags,
              inputs,
              supportedParams: inputs?.map((i: any) => i.name) ?? [],
              createdAt: model.createdAt,
              updatedAt: model.updatedAt,
            };
          } catch (error) {
            console.error(`❌ Failed to fetch details for model ${model.name}:`, error);
            return {
              id: model.id,
              name: model.name,
              type: model.type,
              capabilities: model.capabilities,
              description: model.shortDescription,
              featured: !!model.featured,
              thumbnail: model.thumbnail,
              tags: model.tags,
              inputs: undefined,
              supportedParams: [],
              createdAt: model.createdAt,
              updatedAt: model.updatedAt,
            };
          }
        })
      );

      if (DEBUG.logModelFetch) console.log(`🧊 Found ${detailedModels.length} 3D models:`, detailedModels);

      // Cache the result
      clientCache.setModel3D(detailedModels);
      return detailedModels;
    } catch (error) {
      console.error('❌ Failed to fetch 3D models:', error);
      return [];
    } finally {
      clientCache.model3DLoading = false;
    }
  }

  /**
   * Generate 3D model using Scenario's custom endpoint
   * Supports both img23d (image-to-3D) and txt23d (text-to-3D) models
   * https://docs.scenario.com/docs/3d-model-generation
   */
  async generateModel3D(params: ScenarioModel3DParams): Promise<{ jobId: string }> {
    try {
      if (DEBUG.log3DGeneration) console.log('🧊 Starting 3D generation with params:', params);

      // Extract known params and spread the rest (dynamic params from model.inputs)
      const { modelId, image, prompt, paint, steps, guidanceScale, targetFaceNum, seed, backImage, leftImage, rightImage, ...dynamicParams } = params;

      // Build request body dynamically
      const body: Record<string, unknown> = {
        ...dynamicParams, // Spread dynamic params first (from model.inputs)
      };

      // Add image if provided (required for img23d, optional for txt23d)
      if (image) body.image = image;

      // Add prompt if provided (required for txt23d, optional for img23d)
      if (prompt) body.prompt = prompt;

      // Add optional params with defaults
      body.paint = paint ?? ScenarioConfig.defaults.threeD.paint;
      body.steps = steps ?? ScenarioConfig.defaults.threeD.steps;
      body.guidanceScale = guidanceScale ?? ScenarioConfig.defaults.threeD.guidanceScale;
      body.targetFaceNum = targetFaceNum ?? ScenarioConfig.defaults.threeD.targetFaceNum;
      if (seed !== undefined) body.seed = seed;

      // Add multi-view images if provided (img23d specific)
      if (backImage) body.backImage = backImage;
      if (leftImage) body.leftImage = leftImage;
      if (rightImage) body.rightImage = rightImage;

      const response = await callScenarioAPI({
        method: 'POST',
        path: `/generate/custom/${modelId}`,
        body,
      });

      if (!response.job?.jobId) {
        throw new Error(`3D generation API returned no job ID (model: ${modelId}, response: ${JSON.stringify(response).slice(0, 200)})`);
      }

      const jobId = response.job.jobId;
      if (DEBUG.log3DGeneration) console.log('🧊 3D generation job started:', jobId);

      return { jobId };
    } catch (error) {
      console.error('❌ 3D generation failed:', error);
      throw error;
    }
  }

  /**
   * Poll 3D job status using standard job polling endpoint
   */
  async pollModel3DJob(jobId: string): Promise<ScenarioModel3DJobResponse> {
    try {
      const response = (await callScenarioAPI({
        method: 'GET',
        path: `/jobs/${jobId}`,
      })) as ScenarioModel3DJobResponse;

      return response;
    } catch (error) {
      console.error('❌ 3D job polling failed:', error);
      throw error;
    }
  }
}

/** Singleton instance */
export const scenarioClient = new ScenarioClient();

/** Export the helper function for direct use */
export { callScenarioAPI };
